import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { API, LOGO, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Bot, BookOpen, Users, ListChecks, Building2, Newspaper, ShoppingBag, ArrowRight, Sparkles } from "lucide-react";

const FEATURES = [
  { icon: Bot, title: "Dakota IA", text: "Tu Super Profesor IA experto en normativa DGT, tráfico y seguridad vial. Amable, pedagógico y disponible 24/7." },
  { icon: BookOpen, title: "Biblioteca de recursos", text: "PDFs, guías, manuales, normativa y documentos oficiales DGT organizados y siempre actualizados." },
  { icon: Users, title: "Comunidad de aprendizaje", text: "Grupos de discusión moderados por tu profesor, con resúmenes inteligentes generados por Dakota." },
  { icon: ListChecks, title: "Test inteligentes", text: "Tests por temas con ayuda inteligente: cuando falles, Dakota te explica el error para aprender de verdad." },
  { icon: Building2, title: "Formación por franquicias", text: "Cada sede Natur con su propio espacio, profesorado y contenidos. Datos seguros y segregados." },
  { icon: Newspaper, title: "Blog educativo", text: "Artículos de actualidad DGT, consejos de conducción y novedades, auditados por profesorado." },
];

export default function Landing() {
  const [blogs, setBlogs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/blogs/public`).then((r) => setBlogs(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-neutral-900">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 h-28 flex items-center justify-between">
          <img src={LOGO} alt="Natur Autoescuela" className="h-28 -my-2 w-auto object-contain" data-testid="landing-logo" />
          <div className="flex items-center gap-3">
            <a href="https://naturautoescuela.es" target="_blank" rel="noreferrer"
               className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-orange-600 transition-colors"
               data-testid="landing-store-link">
              <ShoppingBag size={16} /> Ir a la tienda
            </a>
            <Link to="/auth" data-testid="landing-login-btn">
              <Button className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">Acceso alumnos</Button>
            </Link>
            <a href="https://naturautoescuela.es" target="_blank" rel="noreferrer" data-testid="landing-info-btn">
              <Button variant="outline" className="rounded-full border-neutral-300">Solicitar información</Button>
            </a>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-orange-700 bg-orange-100 rounded-full px-4 py-1.5 mb-6">
            <Sparkles size={14} /> Plataforma educativa Natur
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
            La Academia Digital de <span className="text-orange-600">Natur Autoescuela</span>
          </h1>
          <p className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-xl">
            Aprende, practica test y mejora tus conocimientos con el apoyo de profesorado y Dakota IA.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/auth" data-testid="hero-login-btn">
              <Button size="lg" className="rounded-full bg-orange-600 hover:bg-orange-700 text-white px-8">
                Acceso alumnos <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <a href="https://naturautoescuela.es" target="_blank" rel="noreferrer" data-testid="hero-info-btn">
              <Button size="lg" variant="outline" className="rounded-full border-neutral-300 px-8">Solicitar información</Button>
            </a>
            <a href="https://naturautoescuela.es" target="_blank" rel="noreferrer" data-testid="hero-store-btn">
              <Button size="lg" variant="ghost" className="rounded-full text-orange-700 hover:bg-orange-50 px-8">
                <ShoppingBag size={16} className="mr-2" /> Ir a la tienda
              </Button>
            </a>
          </div>
          <p className="mt-4 text-sm text-neutral-500">
            El acceso es únicamente mediante credenciales facilitadas por Natur Autoescuela.
          </p>
        </div>
        <div className="relative fade-up hidden lg:block">
          <img src="https://images.unsplash.com/photo-1491975474562-1f4e30bc9468?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
               alt="Alumna estudiando" className="rounded-2xl shadow-2xl object-cover w-full h-[440px]" />
          <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl border border-neutral-200 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center text-white"><Bot size={20} /></div>
            <div>
              <p className="text-sm font-semibold">Dakota IA</p>
              <p className="text-xs text-neutral-500">Tu tutor disponible 24/7</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-neutral-200 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-12">Todo lo que necesitas para aprobar</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <div key={i} data-testid={`feature-card-${i}`}
                   className="border border-neutral-200 rounded-xl p-8 bg-[#FAFAFA] hover:shadow-lg hover:-translate-y-1 transition-transform">
                <div className="w-11 h-11 rounded-lg bg-orange-100 text-orange-700 flex items-center justify-center mb-4">
                  <f.icon size={22} />
                </div>
                <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {blogs.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-20">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-8">Blog educativo</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {blogs.map((b) => (
              <article key={b.id} className="border border-neutral-200 bg-white rounded-xl p-6" data-testid="public-blog-card">
                <p className="text-xs text-neutral-500 mb-2">{fmtDate(b.date)} · {b.author_name}</p>
                <h3 className="font-semibold text-lg leading-snug mb-2">{b.h1}</h3>
                <p className="text-sm text-neutral-600 line-clamp-3">{b.meta_description}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="bg-neutral-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Empieza hoy tu aprendizaje con Natur Academy</h2>
          <p className="mt-4 text-neutral-400">El acceso se facilita al contratar tu formación con Natur Autoescuela.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/auth" data-testid="cta-login-btn">
              <Button size="lg" className="rounded-full bg-orange-600 hover:bg-orange-500 text-white px-10">
                Acceso alumnos
              </Button>
            </Link>
            <a href="https://naturautoescuela.es" target="_blank" rel="noreferrer" data-testid="cta-info-btn">
              <Button size="lg" variant="outline" className="rounded-full border-neutral-600 text-white bg-transparent hover:bg-neutral-800 px-10">
                Solicitar información
              </Button>
            </a>
          </div>
        </div>
      </section>

      <footer className="py-10 text-center text-sm bg-neutral-950 text-neutral-400">
        <img src={LOGO} alt="Natur Autoescuela" className="h-14 mx-auto mb-4 bg-white rounded-xl px-4 py-1.5 object-contain" />
        © {new Date().getFullYear()} Natur Academy · Ecosistema Natur Autoescuela ·{" "}
        <a href="https://naturautoescuela.es" className="text-orange-500 hover:underline" target="_blank" rel="noreferrer">naturautoescuela.es</a>
      </footer>
    </div>
  );
}
